import { useCallback } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent,
  FormHelperText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import distributionApi from 'services/api/distribution';
import { ReleaseSongFieldType } from 'services/store/distribution/types';
import {
  selectReleaseSongHasVocals,
  selectReleaseSongLanguage,
} from 'services/store/distribution/selectors';
import LoadingAdornment from 'pages/Distribution/components/LoadingAdornment';
import { LANGUAGES } from './languages';

type PropsType = {
  releaseId: string;
};

const Language = ({ releaseId }: PropsType) => {
  const { t } = useTranslation();
  const language = useSelector(selectReleaseSongLanguage(releaseId));
  const hasVocals = useSelector(selectReleaseSongHasVocals(releaseId));

  const { data: languageData, loading: languageLoading, error } = language;
  const { data: hasVocalsData, loading: hasVocalsLoading } = hasVocals;

  const isDisabled = languageLoading || hasVocalsLoading || !hasVocalsData;

  const [updateReleaseSong] = distributionApi.useUpdateReleaseSongMutation();

  const sortedLanguages = Object.entries(LANGUAGES).sort(([, a], [, b]) =>
    a.localeCompare(b)
  );

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      const { value } = event.target;
      updateReleaseSong({
        releaseId,
        field: ReleaseSongFieldType.Language,
        value,
      });
    },
    [releaseId, updateReleaseSong]
  );

  return (
    <FormControl fullWidth disabled={isDisabled} error={!!error}>
      <InputLabel id="language-label">
        {t(
          `distribution:tracklist:fields:${ReleaseSongFieldType.Language}:placeholder`
        )}
      </InputLabel>
      <Select
        labelId="language-label"
        label={t(
          `distribution:tracklist:fields:${ReleaseSongFieldType.Language}:placeholder`
        )}
        value={languageData || ''}
        error={!!error}
        endAdornment={languageLoading && <LoadingAdornment />}
        inputProps={{ ...(languageLoading && { IconComponent: () => null }) }}
        onChange={handleChange}
      >
        {sortedLanguages.map(([languageKey, languageValue]) => (
          <MenuItem key={languageKey} value={languageKey}>
            {languageValue}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
};

export default Language;
